import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Trash2, Plus } from 'lucide-react';
import { SearchableProductDropdown } from './SearchableProductDropdown';

export interface HarvestSeasonProduct {
  id: string;
  productName: string;
  estimatedYield?: string;
}

interface HarvestDateFieldsProps {
  harvestStartDate?: string;
  harvestEndDate?: string;
  harvestSeasonProducts?: HarvestSeasonProduct[];
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onHarvestSeasonProductsChange: (products: HarvestSeasonProduct[]) => void;
}

export const HarvestDateFields: React.FC<HarvestDateFieldsProps> = ({
  harvestStartDate,
  harvestEndDate,
  harvestSeasonProducts = [],
  onStartDateChange,
  onEndDateChange,
  onHarvestSeasonProductsChange
}) => {
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Initialize with default product if empty
  React.useEffect(() => {
    if (harvestSeasonProducts.length === 0) {
      const defaultProduct: HarvestSeasonProduct = {
        id: generateId(),
        productName: '',
        estimatedYield: ''
      };
      onHarvestSeasonProductsChange([defaultProduct]);
    }
  }, []);

  const addHarvestSeasonProduct = () => {
    const newProduct: HarvestSeasonProduct = {
      id: generateId(),
      productName: '',
      estimatedYield: ''
    };
    onHarvestSeasonProductsChange([...harvestSeasonProducts, newProduct]);
  };

  const removeHarvestSeasonProduct = (id: string) => {
    onHarvestSeasonProductsChange(harvestSeasonProducts.filter(product => product.id !== id));
  };

  const updateHarvestSeasonProduct = (id: string, field: keyof HarvestSeasonProduct, value: string) => {
    onHarvestSeasonProductsChange(
      harvestSeasonProducts.map(product =>
        product.id === id ? { ...product, [field]: value } : product
      )
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-base font-medium">Harvest Season</Label>
        <p className="text-sm text-muted-foreground">
          Define the main harvest period and primary products for this farm.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="harvestStartDate">Harvest Start Date</Label>
          <Input
            id="harvestStartDate"
            type="date"
            value={harvestStartDate || ''}
            onChange={(e) => onStartDateChange(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="harvestEndDate">Harvest End Date</Label>
          <Input
            id="harvestEndDate"
            type="date"
            value={harvestEndDate || ''}
            onChange={(e) => onEndDateChange(e.target.value)}
            min={harvestStartDate || undefined}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">Harvest Season Products</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addHarvestSeasonProduct}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Product
          </Button>
        </div>

        {harvestSeasonProducts.map((product, index) => (
          <Card key={product.id}>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Product {index + 1}</Label>
                {harvestSeasonProducts.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeHarvestSeasonProduct(product.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`harvest-product-${product.id}`}>Product Name *</Label>
                  <SearchableProductDropdown
                    value={product.productName}
                    onValueChange={(value) => updateHarvestSeasonProduct(product.id, 'productName', value)}
                    placeholder="Search and select product"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`harvest-yield-${product.id}`}>Estimated Yield (kg)</Label>
                  <Input
                    id={`harvest-yield-${product.id}`}
                    type="number"
                    step="0.1"
                    value={product.estimatedYield || ''}
                    onChange={(e) => updateHarvestSeasonProduct(product.id, 'estimatedYield', e.target.value)}
                    placeholder="e.g., 500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};